import { useState, useEffect } from 'react';
import { Button, Card, Space, Switch, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector} from 'react-redux';
import axios from 'axios';
import queryString from 'query-string';
import { APIClient } from '../../helpers/api_helper';
import IntentsTable from '../../Common/IntentsTable';
import IntentDrawer from '../../Common/IntentDrawer';
import ConfirmModal from '../../Common/ConfirmModal';
import IntentMergeModal, { IntentMergeButton } from '../../Common/IntentMergeModal';
import ProjectIntentsByCategoryView from './ProjectIntentsByCategoryView';
import utils from '../../Common/utils';
import { setInitialFilterConf, setFilterData, updateFilterData } from '../../slices/filters/thunk';
import { useIntentSelection } from '../../Common/Hooks/IntentSelectionHooks';

const api = new APIClient();


const ProjectIntentsTab = ({ projData, isActive, onVersionCreated }) => {

  const dispatch = useDispatch();
  const filterdata = useSelector((state) => state.Filterdata.data)
  const [intentsData, setIntentsData] = useState({});
  const [groupedData, setGroupedData] = useState([]);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [editedIntentId, setEditedIntentId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const selection = useIntentSelection({
    filters: { ...filterdata, projects: [projData.id] },
    total: intentsData?.pagination?.count,
    pageIds: intentsData?.results?.map(r => r.id),
  });
  const { selectedIds, setSelectedIds, allMatching, setAllMatching, count: selectedCount } = selection;
  const [massEditOpen, setMassEditOpen] = useState(false);
  const [showDeletionConfirmModal, setShowDeletionConfirmModal] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(false);


  // `silent` skips the table spinner: an inline cell save reloads to pick up
  // its side effects, and the row already shows the saved value.
  const loadIntents = (f, { silent = false } = {}) => {
    const params = utils.cleanEmpty({
      ...f,
      projects: [projData.id],
    });
    if (!silent) setLoading(true);
    api.get('/api/intents/', params)
      .then(resp => setIntentsData(resp))
      .finally(() => setLoading(false));
  };

  const loadGroupedIntents = (f, { silent = false } = {}) => {
    const params = utils.cleanEmpty({
      ...f,
      projects: [projData.id],
    });
    if (!silent) setLoading(true);
    api.get('/api/intents/grouped_by_category/', params)
      .then(resp => setGroupedData(resp || []))
      .finally(() => setLoading(false));
  };

  const reload = (opts) => {
    if (!projData.id) return;
    if (groupByCategory) {
      loadGroupedIntents(filterdata, opts);
    } else {
      loadIntents(filterdata, opts);
    }
  };

  const handleExport = () => {
    const scope = !allMatching && selectedIds.length > 0
      ? { ids: selectedIds.join(',') }
      : { ...filterdata, projects: [projData.id] };
    const qs = queryString.stringify(utils.cleanEmpty(scope));
    setExportLoading(true);
    axios.get(`/api/intents/export_xlsx/?${qs}`, { responseType: 'blob' })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'intents.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      })
      .finally(() => setExportLoading(false));
  };

  const handleConfirmedDelete = () => {
    const { qs, body, count } = selection.scope;
    setDeleting(true);
    api.delete(`/api/intents/delete_bulk/${qs}`, body, { successMsg: `Uspešno izbrisanih ${count} transakcij` })
      .then(() => {
        setShowDeletionConfirmModal(false);
        selection.clear();
        reload();
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => setDeleting(false));
  };

  useEffect(() => {
    if (!isActive) return;
    dispatch(setInitialFilterConf({
      data: {
        'date_range': null,
      },
      hidden: ['projects'],
      disabled: ['projects'],
    }))
    dispatch(setFilterData(utils.getObjectFromURL()))
  }, [isActive]);

  useEffect(() => {
    if (!projData.id) return;
    if (groupByCategory) {
      loadGroupedIntents(filterdata);
    } else {
      loadIntents(filterdata);
    }
  }, [projData.id, filterdata, groupByCategory]);

  return (
    <>
      <IntentDrawer
        open={drawerOpen}
        editedIntentId={editedIntentId}
        defaultValues={{ project: projData.id }}
        onDone={() => { setDrawerOpen(false); reload(); }}
        onCancel={() => setDrawerOpen(false)}
        onVersionCreated={onVersionCreated}
      />
      <IntentDrawer
        open={massEditOpen}
        massEdit={true}
        massEditScope={selection.scope}
        onCancel={() => setMassEditOpen(false)}
        onDone={() => {
          setMassEditOpen(false);
          selection.clear();
          reload();
        }}
        editedIntentId={null}
      />
      <div style={{ marginBottom: 12 }}>
        <Space>
          <span>Prikaži po kategorijah</span>
          <Switch
            checked={groupByCategory}
            onChange={(checked) => {
              // The grouped view has no checkboxes; don't leave an invisible
              // selection armed behind the bulk buttons.
              selection.clear();
              setGroupByCategory(checked);
            }}
          />
        </Space>
      </div>
      <Card
        extra={
          <Space>
            <Tooltip title="Množično urejanje">
              <Button
                disabled={selectedCount === 0}
                onClick={() => setMassEditOpen(true)}
                icon={<EditOutlined />}
              />
            </Tooltip>
            {!groupByCategory && (
              <IntentMergeButton
                ids={selection.explicitIds}
                onClick={() => setMergeOpen(true)}
              />
            )}
            <Tooltip title="Izbriši izbrane">
              <Button
                disabled={selectedCount === 0}
                onClick={() => setShowDeletionConfirmModal(true)}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
            <Tooltip title="Izvozi v Excel">
              <Button
                loading={exportLoading}
                disabled={exportLoading}
                onClick={handleExport}
                icon={<DownloadOutlined />}
              />
            </Tooltip>
            <Tooltip title="Dodaj transakcijo">
              <Button
                onClick={() => {
                  setEditedIntentId(null);
                  setDrawerOpen(true);
                }}
                icon={<PlusOutlined />}
              />
            </Tooltip>
          </Space>
        }
      >
        {groupByCategory ? (
          <ProjectIntentsByCategoryView
            groupedData={groupedData}
            loading={loading}
            onIntentEdit={intent => { setEditedIntentId(intent.id); setDrawerOpen(true); }}
            onIntentChanged={() => reload({ silent: true })}
          />
        ) : (
          <IntentsTable
            intentsData={intentsData}
            loading={loading}
            page={filterdata.page}
            onPageChange={p => dispatch(updateFilterData({page: p}))}
            onIntentEdit={intent => { setEditedIntentId(intent.id); setDrawerOpen(true); }}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            allMatchingSelected={allMatching}
            setAllMatchingSelected={setAllMatching}
            onIntentChanged={() => reload({ silent: true })}
          />
        )}
      </Card>
      <IntentMergeModal
        open={mergeOpen}
        ids={selection.explicitIds}
        onCancel={() => setMergeOpen(false)}
        onMerged={() => {
          setMergeOpen(false);
          selection.clear();
          reload();
        }}
      />
      <ConfirmModal
        textComponent={<p>{allMatching
          ? `Ali ste prepričani, da želite izbrisati vseh ${selectedCount} transakcij, ki ustrezajo filtrom (vse strani)?`
          : `Ali ste prepričani, da želite izbrisati ${selectedCount} transakcij?`}</p>}
        open={showDeletionConfirmModal}
        confirmBtnTitle='Izbriši'
        confirmBtnProps={{ danger: true }}
        loading={deleting}
        onOk={() => handleConfirmedDelete()}
        onCancel={() => setShowDeletionConfirmModal(false)}
      />
    </>
  );
};

export default ProjectIntentsTab;
